import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { ChevronRight, FileText, Loader2 } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { Card, CardContent } from '@/components/atoms/card';
import { formatCurrency } from '@/components/molecules/financialImpact';
import { formatDateOnly } from '@/lib/dates/date-only';
import { hasLivePortalSession } from '@/stores/portalAuth.store';
import { usePortalEstimates } from '@/hooks/portal/usePortalEstimates';
import { usePortalProposals } from '@/hooks/portal/usePortalProposals';
import { usePortalIdentity } from '@/hooks/portal/usePortalAuth';
import { PortalShell } from '@/components/portal/PortalShell';
import { PortalStatusPill, type PortalPillTone } from '@/components/portal/PortalStatusPill';

export const Route = createFileRoute('/(portal)/portal/estimates/')({
  beforeLoad: () => {
    if (!hasLivePortalSession()) {
      throw redirect({ to: '/portal/login' });
    }
  },
  component: PortalEstimatesPage,
});

const STATUS: Record<string, { label: string; tone: PortalPillTone }> = {
  SENT: { label: 'Awaiting your approval', tone: 'accent' },
  VIEWED: { label: 'Awaiting your approval', tone: 'accent' },
  FOLLOW_UP: { label: 'Awaiting your approval', tone: 'accent' },
  APPROVED: { label: 'Approved', tone: 'success' },
  CONVERTED: { label: 'Approved', tone: 'success' },
  RESOLVED: { label: 'Approved', tone: 'success' },
  DECLINED: { label: 'Declined', tone: 'neutral' },
  EXPIRED: { label: 'Expired', tone: 'neutral' },
};

function statusOf(status: string): { label: string; tone: PortalPillTone } {
  return STATUS[status] ?? { label: status.toLowerCase(), tone: 'neutral' };
}

export function PortalEstimatesPage() {
  const estimates = usePortalEstimates();
  const proposals = usePortalProposals();
  const identity = usePortalIdentity();
  const tenantName = identity.data?.tenant.name;
  const documents: EstimateDocument[] = [
    ...(estimates.data ?? []).map((estimate) => ({
      ...estimate,
      kind: 'estimate' as const,
      number: estimate.estimateNumber,
    })),
    ...(proposals.data ?? []).map((proposal) => ({
      ...proposal,
      kind: 'proposal' as const,
      number: proposal.proposalNumber,
      title: null,
    })),
  ];
  const loading = estimates.isPending || proposals.isPending;
  const hasError = estimates.isError || proposals.isError;
  const retrying = estimates.isFetching || proposals.isFetching;

  return (
    <PortalShell>
      <div className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight">Estimates</h1>
        <p className="text-sm text-muted-foreground">
          {tenantName ? `Quotes from ${tenantName}. ` : ''}Review your estimates and proposal
          options.
        </p>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="size-6 animate-spin text-muted-foreground" />
        </div>
      ) : null}
      {hasError ? (
        <Card>
          <CardContent className="space-y-3 py-6 text-sm text-muted-foreground">
            <p>We couldn&apos;t load all your estimates and proposals. Please try again.</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if (estimates.isError) void estimates.refetch();
                if (proposals.isError) void proposals.refetch();
              }}
              disabled={retrying}
            >
              {retrying ? <Loader2 className="size-4 animate-spin" /> : null}
              Try again
            </Button>
          </CardContent>
        </Card>
      ) : null}
      {!loading && !hasError && documents.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            You don&apos;t have any estimates yet.
          </CardContent>
        </Card>
      ) : null}
      {!loading && documents.length > 0 ? (
        <Card className="overflow-hidden p-0">
          <div className="divide-y divide-border">
            {documents.map((document) => (
              <EstimateRow key={`${document.kind}:${document.id}`} estimate={document} />
            ))}
          </div>
        </Card>
      ) : null}
    </PortalShell>
  );
}

interface EstimateDocument {
  id: string;
  kind: 'estimate' | 'proposal';
  number: string;
  title: string | null;
  status: string;
  total: number;
  validUntil: string | null;
}

function EstimateRow({ estimate }: { estimate: EstimateDocument }) {
  const status = statusOf(estimate.status);
  return (
    <Link
      to={estimate.kind === 'proposal' ? '/portal/proposals/$id' : '/portal/estimates/$id'}
      params={{ id: estimate.id }}
      className="grid grid-cols-[2.25rem_minmax(0,1fr)_1rem] items-center gap-x-3 gap-y-2 px-4 py-3.5 transition-colors hover:bg-muted/50 sm:flex"
    >
      <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
        <FileText className="size-4" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">
          {estimate.title?.trim() ||
            `${estimate.kind === 'proposal' ? 'Proposal' : 'Estimate'} #${estimate.number}`}
        </p>
        <p className="truncate text-xs text-muted-foreground">
          {estimate.number}
          {estimate.validUntil ? ` · expires ${formatDateOnly(estimate.validUntil)}` : ''}
        </p>
      </div>
      <div className="col-span-2 col-start-2 flex flex-wrap items-center justify-between gap-2 sm:contents">
        <PortalStatusPill label={status.label} tone={status.tone} />
        <span className="shrink-0 text-right text-sm font-semibold tabular-nums sm:w-20">
          {formatCurrency(estimate.total)}
        </span>
      </div>
      <ChevronRight className="col-start-3 row-start-1 size-4 shrink-0 text-muted-foreground" />
    </Link>
  );
}
